import { useCallback, useEffect, useState } from "react";

/**
 * Height that makes an element reach the bottom of the window.
 *
 * The designer wants every pixel below the toolbar: it is a canvas, and a
 * canvas half the height of the screen shows half the process. Subtracting a
 * guessed constant from `100vh` is what it did first, and the guess was wrong
 * by ~120px because the app layout puts a header *and* a tab strip above the
 * page — leaving a 454px canvas that fitted a nine-step process at 24% zoom.
 *
 * Measuring instead of guessing survives whatever chrome the layout grows next,
 * and re-measures on resize.
 *
 * `gap` is the breathing room left under the element. It is also the slack that
 * absorbs the last few pixels of layout the walk below cannot see — collapsed
 * margins and sub-pixel rounding — so the page lands just under the fold rather
 * than just over it. A canvas that scrolls the *page* is much worse than one
 * with a small margin beneath it: the wheel stops meaning zoom.
 */
export const useFillViewport = (gap = 26) => {
  const [node, setNode] = useState<HTMLElement | null>(null);
  const [height, setHeight] = useState<number>();

  const ref = useCallback((element: HTMLElement | null) => setNode(element), []);

  useEffect(() => {
    if (!node) return;
    const measure = () => {
      const rect = node.getBoundingClientRect();
      // The element's position in the *document*, not the viewport. Measuring
      // `rect.top` alone is scroll-dependent, so the first scroll shortens the
      // apparent distance to the top, the next measure makes the element
      // taller, and the page grows every time you touch the wheel. Adding the
      // scroll back makes the number stable — and, once the height is right,
      // the page stops scrolling at all and the canvas owns the wheel.
      const top = rect.top + window.scrollY;

      // Whatever follows us in the document flow — this layout puts a 66px
      // footer after the page content, and claiming that space is what makes
      // the page scroll by exactly one footer.
      //
      // Measured by walking the following siblings rather than by subtracting
      // our bottom from the document's: `.ant-layout` carries `min-height:
      // 100vh`, so the document stops shrinking when we do and that difference
      // reports back phantom slack that grows every pass. Summing what is
      // actually laid out after us is stable because it does not depend on our
      // own height at all. Absolutely positioned and fixed elements are skipped
      // — drawers and modals overlay the page, they do not take space in it.
      const px = (value: string) => parseFloat(value) || 0;
      let below = 0;
      for (let el: Element | null = node; el && el !== document.body; el = el.parentElement) {
        for (let sib = el.nextElementSibling; sib; sib = sib.nextElementSibling) {
          const style = getComputedStyle(sib);
          if (style.position === "absolute" || style.position === "fixed") continue;
          below +=
            sib.getBoundingClientRect().height +
            px(style.marginTop) +
            px(style.marginBottom);
        }
        // Padding on an ancestor sits below us just as surely as a sibling does.
        if (el.parentElement) {
          below += px(getComputedStyle(el.parentElement).paddingBottom);
        }
      }

      // A floor, so a very short window still leaves something usable to
      // scroll rather than collapsing the canvas to nothing.
      setHeight(Math.max(300, window.innerHeight - top - below - gap));
    };
    measure();
    window.addEventListener("resize", measure);
    // The toolbar above wraps at narrow widths, which moves this element down
    // without a window resize.
    const observer = new ResizeObserver(measure);
    observer.observe(document.body);
    return () => {
      window.removeEventListener("resize", measure);
      observer.disconnect();
    };
  }, [node, gap]);

  return { ref, height };
};
